import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  installServerContent, fetchServerContent,
  fetchServerContentJobs, syncServerContentJob,
  updateServerContentJob, destroyServerContentJob, removeServerContentJob,
  updateServerPack, destroyServerPack
} from "src/actions/mine_craft/serversActions";
import { fetchAddonsCollection } from "src/actions/mine_craft/addonsActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";

import {
  Button, Dropdown, MenuProps, message, Modal, Segmented, Select, Space, Spin, Switch, Tag, Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SyncOutlined
} from '@ant-design/icons';
import Card from "src/componets/Card";
import AlertBlock from "src/componets/AlertBlock";
import { shortFromNow } from "./_serverHelpers";

// Community (Posts) add_type values whose bundles make sense as a
// server install: addons (.mcaddon/.mcpack), worlds (.mcworld) and
// skin packs (.mcpack). Skins / text posts / structures stay out.
const INSTALLABLE_ADD_TYPES = [1, 3, 4];
const ADD_TYPE_NAMES: { [key: number]: string } = {
  1: 'addon',
  3: 'world',
  4: 'skin pack'
};
const SEARCH_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;
// While a job is pending, re-read the list to catch the flip to
// installed/failed — the content.installed webhook (30s sync fallback)
// lands it server-side, but nothing pushes it to this page.
const JOBS_POLL_MS = 10000;

// Install-jobs filter. `all` also covers `queued` and `pending` jobs, which
// have no tab of their own — an install in flight is only ever a moment away
// from one of the terminal states.
type JobFilter = 'all' | 'installed' | 'failed';
const JOB_FILTERS: { value: JobFilter, label: string }[] = [
  { value: 'all',       label: 'All' },
  { value: 'installed', label: 'Installed' },
  { value: 'failed',    label: 'Failed' }
];

// Content management for a hosted server — queueing installs next to the
// jobs they produce, and the packs they land as. Jobs and packs load on
// mount; power controls, stats and the console live in the Console tab.
const _Content = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [busy, setBusy] = useState(false);
  const [addonIds, setAddonIds] = useState<any[]>([]); // labelInValue entries
  const [addonOptions, setAddonOptions] = useState<any[]>([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);
  const [restart, setRestart] = useState(false);
  const [packs, setPacks] = useState<any>(null);
  const [packsLoading, setPacksLoading] = useState(false);
  // The job (`job-<id>`) or untracked pack (its uuid) whose toggle/delete
  // is in flight — one at a time, since each one may restart the server.
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [jobsFilter, setJobsFilter] = useState<JobFilter>('all');
  // Post lookup for job items installed from the Community catalog, keyed by
  // post id — thumbnail + title + the OneSky link, when that post still exists.
  const [postsById, setPostsById] = useState<{ [id: number]: any }>({});

  const searchTimer = useRef<any>(null);
  const requestedPostIds = useRef<Set<number>>(new Set());

  // Debounced remote search on the Community catalog (the /onesky/addons
  // endpoint already matches `term` against title/search_text + author
  // name), narrowed to the installable add_types and the server's app.
  const searchAddons = useCallback((term: string) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    searchTimer.current = setTimeout(async () => {
      if (!server) return;

      setAddonsLoading(true);
      try {
        const { addons, ids } = await fetchAddonsCollection('/onesky/addons', {
          app_id:   server.app_id,
          term:     term || undefined,
          add_type: INSTALLABLE_ADD_TYPES,
          limit:    SEARCH_LIMIT
        });
        setAddonOptions(ids.map((id: number) => addons[id]).filter(Boolean));
      } catch (error) {
        console.error(error);
        setAddonOptions([]);
      }
      setAddonsLoading(false);
    }, SEARCH_DEBOUNCE_MS);
  }, [server]);

  const serverId = server?.id;

  // `silent` is for the pending-job poll: no spinner, and a failed poll
  // keeps the rows already on screen instead of raising an error banner.
  const loadJobs = useCallback(async (silent = false) => {
    if (!serverId) return;

    if (!silent) setJobsLoading(true);
    const resultAction = await dispatch(fetchServerContentJobs(serverId));
    if (!silent) setJobsLoading(false);

    if (fetchServerContentJobs.fulfilled.match(resultAction)) {
      setJobs(resultAction.payload.content_jobs || []);
      setJobsError(null);
    } else if (!silent) {
      // Without this the card falls through to its empty state and claims
      // there are no jobs, when really the list could not be read at all.
      const payload: any = resultAction.payload;
      setJobsError(
        (Array.isArray(payload) ? payload[0] : payload?.errors?.[0]) ||
        resultAction.error?.message ||
        'Install jobs could not be loaded'
      );
    }
  }, [serverId, dispatch]);

  // Silent on failure: a server that never came up has no packs or stats to
  // report, and the status pill already says so — an error toast per panel on
  // every mount would just be noise.
  const loadPacks = useCallback(async (notify = false) => {
    if (!serverId) return;

    setPacksLoading(true);
    const resultAction = await dispatch(fetchServerContent(serverId));
    setPacksLoading(false);

    if (fetchServerContent.fulfilled.match(resultAction)) {
      setPacks(resultAction.payload);
    } else {
      setPacks(null);
      if (notify) {
        const payload: any = resultAction.payload;
        message.error(Array.isArray(payload) ? payload[0] : 'Packs unavailable');
      }
    }
  }, [serverId, dispatch]);

  const ready = hosted?.state === 'ready';
  const isHosted = !!hosted;

  useEffect(() => {
    if (!isHosted) return;

    loadJobs().catch(console.error);

    // Packs proxy through to Wings, which only answers once provisioning
    // finished and the container is up.
    if (ready) {
      loadPacks().catch(console.error);
    }
  }, [isHosted, ready, loadJobs, loadPacks]);

  // The backend sends one job at a time per server, so a `queued` job is as
  // live as a `pending` one — both keep the poll going.
  const pendingCount = jobs.filter(job => job.state === 'pending' || job.state === 'queued').length;
  const prevPendingCount = useRef(0);

  useEffect(() => {
    if (!isHosted || !pendingCount) return;

    const timer = setInterval(() => {
      loadJobs(true).catch(console.error);
    }, JOBS_POLL_MS);

    return () => clearInterval(timer);
  }, [isHosted, pendingCount, loadJobs]);

  // A job leaving `pending` means the server's pack list just changed —
  // re-read it. The job itself already names the packs it landed: the
  // backend links them in the same write that marks it installed.
  useEffect(() => {
    const finished = prevPendingCount.current > pendingCount;

    prevPendingCount.current = pendingCount;

    if (!finished || !ready) return;

    loadPacks().catch(console.error);
  }, [pendingCount, ready, loadPacks]);

  // Jobs only carry the post id (see HostedContentJob#post_id) — batch-fetch
  // the Community post behind each one still unseen, so the job list can show
  // a thumbnail and a link instead of a bare "addon #id". Ids already requested
  // (found or not) are skipped, so a refresh re-hitting the same jobs never
  // re-fetches.
  useEffect(() => {
    if (!server) return;

    const ids = Array.from(new Set(
      jobs.map((job: any) => job.post_id).filter(Boolean)
    )).filter(id => !requestedPostIds.current.has(id));

    if (!ids.length) return;

    ids.forEach(id => requestedPostIds.current.add(id));

    fetchAddonsCollection('/onesky/addons', { app_id: server.app_id, id: ids, limit: ids.length })
      .then(({ addons }) => setPostsById(prev => ({ ...prev, ...addons })))
      .catch(console.error);
  }, [server, jobs]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Content installs are only available for hosted servers.</div>
        </div>
      </Card>
    );
  }

  async function install() {
    if (!server) return;

    const data: any = { restart };
    if (addonIds.length) data.post_ids = addonIds.map(entry => entry.value);
    if (urls.length)     data.items = urls.map(url => ({ url, type: 'auto' }));

    setBusy(true);
    const resultAction = await dispatch(installServerContent({ id: server.id, data }));
    setBusy(false);

    if (installServerContent.fulfilled.match(resultAction)) {
      // 202 even when the hosting service refused the first item — the
      // refusal comes back on that row.
      const rows: any[] = resultAction.payload?.content_jobs || [];
      const refused = rows.find(row => row.state === 'failed');

      if (refused) {
        message.error(refused.error || 'Install rejected');
      } else {
        message.success(rows.length > 1 ? `${rows.length} installs queued` : 'Install job queued');
      }
      setAddonIds([]);
      setUrls([]);
      loadJobs().catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Install failed');
    }
  }

  async function syncJob(jobId: number) {
    if (!server) return;

    const resultAction = await dispatch(syncServerContentJob({ id: server.id, jobId }));

    if (syncServerContentJob.fulfilled.match(resultAction)) {
      loadJobs().catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Refresh failed');
    }
  }

  // Both honour the "Restart to apply changes" switch of the install card:
  // without it crafty stages the change for the next restart.
  const appliedNote = (restarted: boolean) =>
    restarted ? 'restarting now' : 'applies on the next restart';

  const jobKey = (job: any) => `job-${job.id}`;

  const jobLabel = (job: any): string =>
    postsById[job.post_id]?.title ||
    job.world_name ||
    String(job.url || '').split('?')[0].split('/').pop() ||
    'Content';

  // A job's packs move together: both calls ride one request, and the
  // backend restarts once, after the last pack.
  async function toggleJob(job: any, active: boolean) {
    if (!server) return;

    setActionBusy(jobKey(job));
    const resultAction = await dispatch(updateServerContentJob({ id: server.id, jobId: job.id, active, restart }));
    setActionBusy(null);

    if (updateServerContentJob.fulfilled.match(resultAction)) {
      const restarted = !!resultAction.payload?.restarted;
      message.success(`${jobLabel(job)} ${active ? 'enabled' : 'disabled'} — ${appliedNote(restarted)}`);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Pack change failed');
    }
    loadPacks().catch(console.error);
  }

  // A refusal halfway leaves the job with the packs still on the server,
  // so the jobs are re-read either way.
  async function deleteJob(job: any) {
    if (!server) return;

    setActionBusy(jobKey(job));
    const resultAction = await dispatch(destroyServerContentJob({ id: server.id, jobId: job.id, restart }));
    setActionBusy(null);

    if (destroyServerContentJob.fulfilled.match(resultAction)) {
      const restarted = !!resultAction.payload?.restarted;
      message.success(`${jobLabel(job)} deleted — ${appliedNote(restarted)}`);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Delete failed');
    }
    loadJobs(true).catch(console.error);
    loadPacks().catch(console.error);
  }

  async function removeJob(job: any) {
    if (!server) return;

    setActionBusy(jobKey(job));
    const resultAction = await dispatch(removeServerContentJob({ id: server.id, jobId: job.id }));
    setActionBusy(null);

    if (removeServerContentJob.fulfilled.match(resultAction)) {
      setJobs(prev => prev.filter(row => row.id !== job.id));
      message.success(`${jobLabel(job)} removed from the list`);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Remove failed');
      loadJobs(true).catch(console.error);
    }
  }

  function confirmRemoveJob(job: any) {
    Modal.confirm({
      title: `Remove ${jobLabel(job)} from the list?`,
      icon: <ExclamationCircleFilled />,
      content: 'Only this record goes — nothing changes on the server.',
      okText: 'Remove',
      onOk: () => removeJob(job)
    });
  }

  function confirmDeleteJob(job: any, rowPacks: any[]) {
    Modal.confirm({
      title: `Delete ${jobLabel(job)}?`,
      icon: <ExclamationCircleFilled />,
      content: (
        <>
          <p>Removes its packs from the world and deletes their files — reinstall from Community or a URL to get them back.</p>
          <ul>{rowPacks.map(pack => <li key={pack.uuid}>{packLabel(pack)}</li>)}</ul>
        </>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => deleteJob(job)
    });
  }

  async function togglePack(pack: any, active: boolean) {
    if (!server) return;

    setActionBusy(pack.uuid);
    const resultAction = await dispatch(updateServerPack({ id: server.id, packId: pack.uuid, active, restart }));
    setActionBusy(null);

    if (updateServerPack.fulfilled.match(resultAction)) {
      const restarted = !!resultAction.payload?.restarted;
      message.success(`${pack.name || 'Pack'} ${active ? 'enabled' : 'disabled'} — ${appliedNote(restarted)}`);
      loadPacks().catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Pack change failed');
    }
  }

  async function deletePack(pack: any) {
    if (!server) return;

    setActionBusy(pack.uuid);
    const resultAction = await dispatch(destroyServerPack({ id: server.id, packId: pack.uuid, restart }));
    setActionBusy(null);

    if (destroyServerPack.fulfilled.match(resultAction)) {
      const restarted = !!resultAction.payload?.restarted;
      message.success(`${pack.name || 'Pack'} deleted — ${appliedNote(restarted)}`);
      loadPacks().catch(console.error);
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Pack delete failed');
    }
  }

  // A Popconfirm inside the dropdown would close along with the menu.
  function confirmDeletePack(pack: any) {
    Modal.confirm({
      title: `Delete ${pack.name || 'this pack'}?`,
      icon: <ExclamationCircleFilled />,
      content: 'Removes it from the world and deletes its files — reinstall from Community or a URL to get it back.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => deletePack(pack)
    });
  }

  // One installed pack: { uuid, name, version: [1, 0, 0], kind, folder,
  // active }. Inactive packs stay on disk but are not enabled in the
  // current world.
  const livePacks: any[] = (packs?.packs || []).filter((pack: any) => pack.uuid);
  const packsByUuid: { [uuid: string]: any } = Object.fromEntries(livePacks.map(pack => [pack.uuid, pack]));
  // A job lands at most one behavior and one resource pack.
  const jobPackUuids = (job: any): string[] =>
    [job.behavior_pack_uuid, job.resource_pack_uuid].filter(Boolean);
  const linkedUuids = new Set(jobs.flatMap(jobPackUuids));
  // Packs no listed job claims: installed before jobs recorded their packs,
  // or by a job past the list's 50-row window. A reinstall's packs stay with
  // the job that first installed them.
  const untrackedPacks = livePacks.filter(pack => !linkedUuids.has(pack.uuid));

  const jobPacks = (job: any): any[] =>
    jobPackUuids(job).map(uuid => packsByUuid[uuid]).filter(Boolean);

  const visibleJobs = jobsFilter === 'all'
    ? jobs
    : jobs.filter(job => job.state === jobsFilter);
  const visibleUntracked = jobsFilter === 'failed' ? [] : untrackedPacks;
  const rowsTotal = jobs.length + untrackedPacks.length;

  const packVersion = (pack: any) =>
    Array.isArray(pack.version) ? pack.version.join('.') : pack.version;

  const packLabel = (pack: any) => {
    const version = packVersion(pack);

    return [
      pack.kind === 'behavior' ? 'BP' : 'RP',
      pack.name || pack.uuid,
      version && `v${version}`
    ].filter(Boolean).join(' · ');
  };

  // Tooltips and menus below are rendered by plain functions, not inline
  // components: a new component type per render would remount them on every
  // poll, closing an open menu or tooltip under the cursor.
  const renderJobState = (state: string, error?: string) => {
    let tag;

    switch (state) {
      case 'queued':
        tag = <Tag icon={<ClockCircleOutlined />}>queued</Tag>;
        break;
      case 'pending':
        tag = <Tag icon={<SyncOutlined spin />} color='processing'>pending</Tag>;
        break;
      case 'installed':
        tag = <Tag icon={<CheckCircleOutlined />} color='success'>installed</Tag>;
        break;
      case 'failed':
        tag = <Tag icon={<CloseCircleOutlined />} color='error'>failed</Tag>;
        break;
      default:
        tag = <Tag>{state}</Tag>;
    }

    return error
      ? <Tooltip title={error}><span className='o-tip'>{tag}</span></Tooltip>
      : tag;
  }

  const renderPacksState = (rowPacks: any[]) => {
    const inactive = rowPacks.filter(pack => pack.active === false).length;

    if (!inactive) return null;

    return (
      <Tag>
        {inactive === rowPacks.length ? 'disabled' : `${inactive} of ${rowPacks.length} disabled`}
      </Tag>
    );
  };

  // `needsServer`: the actions call crafty, so the server has to be ready.
  const renderActionsButton = (items: MenuProps['items'], busy: boolean, needsServer = true) => (
    <Dropdown
      trigger={['click']}
      placement='bottomRight'
      disabled={(needsServer && !ready) || !!actionBusy}
      menu={{ items }}
    >
      <Button
        size='small'
        type='text'
        icon={<EllipsisOutlined />}
        loading={busy}
        aria-label='Pack actions'
      />
    </Dropdown>
  );

  // A settled job holding no packs — a reinstall whose packs stay with the
  // first job, a failed install, a world — can only leave the list.
  const renderRemoveMenu = (job: any) => {
    const items: MenuProps['items'] = [{
      key: 'remove',
      icon: <DeleteOutlined />,
      label: 'Remove from list',
      title: 'Drop this record — nothing changes on the server',
      onClick: () => confirmRemoveJob(job)
    }];

    return renderActionsButton(items, actionBusy === jobKey(job), false);
  };

  // Writers only. A job's behavior and resource pack are enabled, disabled
  // and deleted together, never one by one. A mixed row (one pack disabled)
  // offers both directions.
  const renderJobMenu = (job: any, rowPacks: any[]) => {
    if (!write_minecraft) return null;

    const settled = job.state === 'installed' || job.state === 'failed';
    if (settled && !jobPackUuids(job).length) return renderRemoveMenu(job);

    if (!rowPacks.length) return null;

    const items: MenuProps['items'] = [];

    if (rowPacks.some(pack => pack.active === false)) {
      items.push({
        key: 'enable',
        icon: <PlayCircleOutlined />,
        label: 'Enable',
        title: 'Enable its packs in the current world',
        onClick: () => toggleJob(job, true)
      });
    }
    if (rowPacks.some(pack => pack.active !== false)) {
      items.push({
        key: 'disable',
        icon: <PauseCircleOutlined />,
        label: 'Disable',
        title: 'Disable its packs in the current world (files kept)',
        onClick: () => toggleJob(job, false)
      });
    }
    items.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: () => confirmDeleteJob(job, rowPacks)
    });

    return renderActionsButton(items, actionBusy === jobKey(job));
  };

  // Writers only. Packs no job claims are acted on one by one, by uuid.
  const renderPackMenu = (rowPacks: any[]) => {
    if (!write_minecraft || !rowPacks.length) return null;

    const rowBusy = rowPacks.some(pack => pack.uuid === actionBusy);

    const items: MenuProps['items'] = rowPacks.map(pack => {
      const inactive = pack.active === false;

      return {
        type: 'group' as const,
        key: pack.uuid,
        label: packLabel(pack),
        children: [
          {
            key: `toggle-${pack.uuid}`,
            icon: inactive ? <PlayCircleOutlined /> : <PauseCircleOutlined />,
            label: inactive ? 'Enable' : 'Disable',
            title: inactive ? 'Enable in the current world' : 'Disable in the current world (files kept)',
            onClick: () => togglePack(pack, inactive)
          },
          {
            key: `delete-${pack.uuid}`,
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: () => confirmDeletePack(pack)
          }
        ]
      };
    });

    return renderActionsButton(items, rowBusy);
  };

  // Directory installs (post_id present) link to the post once its lookup
  // resolves; world uploads show their world name; URL installs and posts
  // that no longer exist fall back to plain text — there's nowhere in OneSky
  // to send that link.
  const JobItemChip = ({ item }: { item: any }) => {
    if (item.world_name) {
      return <span className='v-srv-chip' title={item.url}>world · {item.world_name}</span>;
    }

    if (!item.post_id) {
      return <span className='v-srv-chip' title={item.url}>{item.url}</span>;
    }

    const post = postsById[item.post_id];

    if (!post) {
      return <span className='v-srv-chip'>{`addon #${item.post_id}`}</span>;
    }

    return (
      <a
        className='v-srv-chip v-srv-chip--post'
        href={post.url}
        target='_blank'
        rel='noreferrer'
        title={post.title}
      >
        <img className='o-thumb' src={post.image_url} alt='' />
        <span className='o-title'>{post.title}</span>
      </a>
    );
  };

  const PackChip = ({ pack }: { pack: any }) => {
    const version = packVersion(pack);

    return (
      <span className='v-srv-chip v-srv-chip--pack' title={pack.uuid}>
        <span className='o-kind'>{pack.kind === 'behavior' ? 'BP' : 'RP'}</span>
        <span className='o-title'>{pack.name || pack.uuid}</span>
        {version && <span className='o-version'>v{version}</span>}
      </span>
    );
  };

  const filterCount = (filter: JobFilter) => {
    switch (filter) {
      case 'all':       return rowsTotal;
      case 'installed': return jobs.filter(job => job.state === 'installed').length + untrackedPacks.length;
      default:          return jobs.filter(job => job.state === filter).length;
    }
  };

  return (
    <>
      {write_minecraft &&
        <Card className='v-srv-install'>
            <h4 className='v-content-title'>Install content</h4>

          <h6 className='v-content-label o-sm'>Community addons</h6>
          <Select
            mode='multiple'
            labelInValue
            showSearch
            filterOption={false}
            loading={addonsLoading}
            style={{ width: '100%' }}
            placeholder='Search Community by title or author'
            value={addonIds}
            onChange={setAddonIds}
            onSearch={searchAddons}
            onFocus={() => searchAddons('')}
            optionLabelProp='label'
            notFoundContent={addonsLoading ? <Spin size='small' /> : 'No installable addons found'}
          >
            {addonOptions.map(option => {
              const installable = option.published && option.file_url;

              return (
                <Select.Option
                  key={option.id}
                  value={option.id}
                  label={`#${option.id} ${option.title}`}
                  disabled={!installable}
                >
                  <Space>
                    <img
                      src={option.image_url}
                      alt=''
                      style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <span>
                      <b>#{option.id}</b> {option.title}
                      {' '}<Tag>{ADD_TYPE_NAMES[option.add_type] || option.add_type}</Tag>
                      {!option.published && <Tag color='default'>unpublished</Tag>}
                      {!option.file_url && <Tag color='error'>no file</Tag>}
                    </span>
                  </Space>
                </Select.Option>
              );
            })}
          </Select>

          <h6 className='v-content-label o-sm v-mt-15'>External URLs (.mcpack / .mcaddon / .mcworld / .zip)</h6>
          <Select
            mode='tags'
            style={{ width: '100%' }}
            tokenSeparators={[',', ' ']}
            placeholder='https://…'
            value={urls}
            onChange={setUrls}
          />

          <div className='o-footer'>
            <Space>
              <Switch checked={restart} onChange={setRestart} />
              <span title='Also used when you enable, disable or delete a pack below'>Restart to apply changes</span>
            </Space>

            <Button
              type='primary'
              icon={<CloudDownloadOutlined />}
              disabled={busy || !ready || (!addonIds.length && !urls.length)}
              onClick={install}
            >
              Install
            </Button>
          </div>
        </Card>
      }

      <Card className='v-srv-jobs'>
        <div className='o-head'>
          <div className='o-title'>
            <h4 className='v-content-title'>Packs</h4>
            {packs?.levelName && <span className='o-world'>World: {packs.levelName}</span>}
          </div>

          <Button
            className='o-icon-btn'
            icon={<SyncOutlined />}
            title='Refresh'
            aria-label='Refresh packs and install jobs'
            loading={jobsLoading || packsLoading}
            onClick={() => {
              loadJobs().catch(console.error);
              if (ready) loadPacks(true).catch(console.error);
            }}
          />
        </div>

        {!packsLoading && !packs &&
          <div className='o-packs-hint'>
            {ready
              ? 'Packs unavailable — crafty-servers did not return the pack list, try refreshing.'
              : 'The server has to finish provisioning before its packs can be read.'}
          </div>
        }

        <Segmented
          className='o-filter'
          value={jobsFilter}
          onChange={value => setJobsFilter(value as JobFilter)}
          options={JOB_FILTERS.map(filter => ({
            value: filter.value,
            label: (
              <span className='o-filter-label'>
                {filter.label}
                <span className='o-count'>{filterCount(filter.value)}</span>
              </span>
            )
          }))}
        />

        {jobsError &&
          <AlertBlock type='error' message={jobsError} onClose={() => setJobsError(null)} />
        }

        <div className='o-list'>
          {jobsLoading && !jobs.length &&
            [0, 1, 2].map(index => <div key={index} className='v-srv-job o-skeleton' />)
          }

          {visibleJobs.map(job => {
            const at = job.created_at || job.updated_at;
            // A job can land in `failed` with an empty error string; saying so
            // beats an unexplained red-tagged row.
            const error = job.state === 'failed'
              ? (job.error || 'Install failed — crafty-servers gave no reason.')
              : job.error;
            const rowPacks = jobPacks(job);

            return (
              <div key={job.id} className='v-srv-job'>
                <div className='o-head'>
                  <div className='o-left'>
                    {renderJobState(job.state, error)}
                    {renderPacksState(rowPacks)}
                  </div>
                  <div className='o-right'>
                    {job.state === 'pending' && write_minecraft &&
                      <Button
                        size='small'
                        icon={<ReloadOutlined />}
                        title='Refresh job'
                        aria-label='Refresh job'
                        onClick={() => syncJob(job.id)}
                      />
                    }
                    {renderJobMenu(job, rowPacks)}
                  </div>
                </div>

                <div className='o-body'>
                  <div className='o-items'>
                    <JobItemChip item={job} />
                  </div>
                  {at && <span className='o-time' title={at}>{shortFromNow(at)}</span>}
                </div>
              </div>
            );
          })}

          {visibleUntracked.map(pack => (
            <div key={pack.uuid} className='v-srv-job'>
              <div className='o-head'>
                <div className='o-left'>
                  <Tooltip title='Not linked to an install job — installed before jobs recorded their packs, or by a job older than this list.'>
                    <span className='o-tip'><Tag>untracked</Tag></span>
                  </Tooltip>
                  {renderPacksState([pack])}
                </div>
                <div className='o-right'>
                  {renderPackMenu([pack])}
                </div>
              </div>

              <div className='o-body'>
                <div className='o-items'>
                  <PackChip pack={pack} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {!jobsLoading && !jobsError && !visibleJobs.length && !visibleUntracked.length &&
          <div className='v-srv-empty'>
            <InboxOutlined className='o-icon' />
            {rowsTotal
              ? <>
                  <div className='o-title'>No {jobsFilter} content</div>
                  <div className='o-hint'>
                    {rowsTotal} in total — switch to All to see {rowsTotal === 1 ? 'it' : 'them'}.
                  </div>
                </>
              : <>
                  <div className='o-title'>No content installed yet</div>
                  <div className='o-hint'>Content you queue{write_minecraft ? ' above' : ''} will show up here.</div>
                </>
            }
          </div>
        }
      </Card>
    </>
  );
}

export default _Content;
